/* eslint-disable max-statements */

import { ScreenRecognitionContext } from '@phx-husky/screen-recognition';
import { graphql } from 'gatsby';
import { kebabCase } from 'lodash';
import React, { FC, lazy, useContext } from 'react';

import { BodyRenderExternalData } from '~/components/BodyRender/models';
import DetailGroup from '~/components/HCP/DetailGroup/DetailGroup';
import EventSubscriptionButton from '~/components/HCP/EventSubscriptionButton';
import VideoWithDescription from '~/components/HCP/VideoWithDescription/VideoWithDescription';
import { ItemListingCarouselProps } from '~/components/ItemListing/models';
import LoginRegisterBlocks from '~/components/LoginRegisterBlocks';
import { PAGE_ONLY_FOR_SIGNED_IN } from '~/components/LoginRegisterBlocks/constants';
import { mapContentstackTranslations } from '~/context/TranslationContext/mapper';
import { useAuthGuard } from '~/hooks/useAuthGuard';
import { Layout } from '~/layout/Layout';
import { itemListingMapper } from '~/mappers/itemListing';
import {
  createItemListingCarouselMock,
  mapPageDataToCarouselItem,
  PAGE_TYPE,
} from '~/mocks/itemListingCaorusel';
import { addBrandPostfixConditionally } from '~/utils/addBrandPostfixConditionally';
import getLocalTimeData from '~/utils/getLocalTime';
import withContentstackPreview from '~/utils/livePreview';

import { Accordion } from '../../components/HCP/Accordion/index';
import { EventPageData } from './models';
import {
  EventType,
  getEventTypeIconSrc,
  ICON_BY_TYPE,
  isPastEvent,
  mapEventDetails,
  presentersMapper,
} from './utils';

const ItemListing = lazy(() => import('~/components/ItemListing'));

const BASE_CLASS = 'l2-content-page';

export const L2EventPage: FC<EventPageData> = ({
  data: {
    profile,
    contentstackL2EventPage,
    allContentstackL2ResourcePage,
    contentstackTranslations,
    contentstackHeader,
    contentstackNewFooter,
    contentstackHeaderWithSplitter,
    contentstackExtendedHeader,
    contentstackFooterWithSplitter,
    contentstackInfoBar,
    allContentstackItemListingGrid: { nodes: itemListingGrids },
    allContentstackAssets: { nodes: jsonRTEImages },
  },
  pageContext: { uid, locale, seoTitle, brand, fullPath },
}) => {
  if (typeof window === 'undefined')
    console.info('Building:', JSON.stringify({ uid, locale, seoTitle }));

  const { isMobile } = useContext(ScreenRecognitionContext);

  const t = mapContentstackTranslations(contentstackTranslations);

  const {
    seo,
    pageHeader,
    fields,
    categories,
    openGraph,
    event_timestamp,
    event_presenters,
    event_data,
    disabledLayoutBackground,
  } = contentstackL2EventPage;
  const categoryClasses = categories.map(
    ({ title }) => `${BASE_CLASS}--${kebabCase(title)}`
  );

  const isFutureEvent = !isPastEvent(event_timestamp);

  const onlyForSignedIn =
    process.env.GATSBY_PROTECTED_PAGES_ENABLED === 'true' || isFutureEvent;

  const { isLoggedIn } = useAuthGuard(onlyForSignedIn);

  const isAuthPending = onlyForSignedIn && isLoggedIn === null;
  const isAccessDenied = onlyForSignedIn && !isLoggedIn;

  if (isAuthPending) return null;

  if (isAccessDenied) {
    return <LoginRegisterBlocks variant={PAGE_ONLY_FOR_SIGNED_IN} />;
  }

  const timeZone = process.env.GATSBY_HCP_SITE_IANA_TIME_ZONE;

  const eventStartDate = getLocalTimeData(
    event_timestamp.event_start_date,
    timeZone,
    locale
  );

  const eventEndDate = getLocalTimeData(
    event_timestamp.event_end_date,
    timeZone,
    locale
  );

  const eventType = event_data?.event_type ?? EventType.ONLINE;

  const eventTypeIcon = (
    <img src={getEventTypeIconSrc(eventType)} alt={ICON_BY_TYPE[eventType]} />
  );

  const details = mapEventDetails(
    t,
    eventStartDate,
    eventEndDate,
    eventTypeIcon,
    eventType
  );

  const pageHeaderImgUrl =
    pageHeader.imageElement[0]?.image &&
    'url' in pageHeader.imageElement[0].image
      ? pageHeader.imageElement[0].image.url
      : undefined;

  const eventDataImgUrl =
    event_data.event_image?.image && 'url' in event_data.event_image.image
      ? event_data.event_image?.image.url
      : undefined;

  const mediaImageUrl = eventDataImgUrl || pageHeaderImgUrl;

  const externalData: BodyRenderExternalData = {
    locale,
    itemListingGrids,
    jsonRTEImages,
  };

  const event_presenters_data = event_presenters?.[0]?.presenters || [];

  const relatedPages = allContentstackL2ResourcePage?.nodes || [];
  const hasRelatedPages = relatedPages.length > 0;

  let relatedResourcesMappedData: ItemListingCarouselProps | null = null;

  if (hasRelatedPages) {
    const mockItemListingCarousel = createItemListingCarouselMock(
      isMobile,
      t,
      PAGE_TYPE.RESOURCES
    );

    mockItemListingCarousel.items[0].items = relatedPages.map(
      mapPageDataToCarouselItem
    );

    relatedResourcesMappedData = itemListingMapper(
      mockItemListingCarousel,
      uid
    ) as ItemListingCarouselProps;
  }

  const eventSubscribeBtn = isFutureEvent ? (
    <EventSubscriptionButton eventPageUid={uid} />
  ) : null;

  return (
    <Layout
      {...{
        openGraph,
        mainImagePath: pageHeaderImgUrl,
        eventPresenters: event_presenters_data,
        seo,
        disabledLayoutBackground,
        extendedHeaderBasic: contentstackExtendedHeader,
        translations: t,
        headerBasic: contentstackHeader,
        categories,
        headerWithSplitter: contentstackHeaderWithSplitter,
        headerSplitterAdditionalData: {
          externalData,
          currentPageUid: uid,
        },
        footerBasic: contentstackNewFooter,
        footerWithSplitter: contentstackFooterWithSplitter,
        footerSplitterAdditionalData: {
          externalData,
          currentPageUid: uid,
        },
        pageUid: uid,
        locale,
        brand,
        pageHeaderProps: { pageHeader, fields },
        className: [BASE_CLASS, ...categoryClasses].join(' '),
        infoBar: {
          ...contentstackInfoBar,
        },
        fullPath,
        profile,
      }}
    >
      <div className="l2EventPage__content-wrapper">
        <div className="cds-container cds-container--max-grid cds-page-block__content">
          <DetailGroup items={details} trailing={eventSubscribeBtn} />
          <VideoWithDescription
            src={event_data.event_video}
            img={mediaImageUrl}
            description={event_data.event_description}
          />

          {event_presenters_data.length > 0 && (
            <div className="l2EventPage__presenters">
              {` ${t.eventPage.presenters} (${event_presenters_data.length})`}
            </div>
          )}

          {event_presenters_data.length > 0 && (
            <Accordion
              className="l2EventPage__accordion"
              viewVariant="line"
              headlineTag="h2"
              items={presentersMapper(event_presenters_data ?? [])}
              semanticVariant="div"
              expandedIndex={[0]}
              closeIcon={{
                icon: 'arrowUp',
              }}
              openIcon={{
                icon: 'arrowDown',
              }}
            />
          )}
        </div>
      </div>
      <div className="l2EventPage__divider" />
      <div className="l2-event-page__related-resources">
        {hasRelatedPages && relatedResourcesMappedData && (
          <ItemListing {...relatedResourcesMappedData} />
        )}
      </div>
    </Layout>
  );
};

export const query = graphql`
  query L2EventPage(
    $id: String
    $locale: String
    $brand: [String]
    $jsonRTEImgUids: [String]
    $categoryUids: [String]
  ) {
    profile: contentstackProfile {
      ...ProfileFragment
    }
    contentstackL2EventPage(id: { eq: $id }) {
      uid
      updated_at
      publish_details {
        time
      }
      fields {
        ...PageFieldsFragment
      }
      seo {
        ...SeoFragment
      }
      openGraph: open_graph {
        ...OpenGraphFragment
      }
      disabledLayoutBackground: disabled_layout_background
      pageHeader: page_header {
        ...PageHeaderFragment
      }
      categories {
        title
        use_in_lables
      }
      event_timestamp {
        event_end_date
        event_start_date
      }
      event_presenters {
        presenters {
          presenter_last_name
          presenter_name
          country
          presenter_image {
            image {
              url
            }
            alt
          }
          id
          about_presenter
          city
        }
      }
      event_data {
        event_description
        event_video
        event_image {
          image {
            url
          }
        }
        event_type
      }
    }
    allContentstackItemListingGrid {
      nodes {
        ...ItemListingGridFragment
      }
    }
    contentstackTranslations(publish_details: { locale: { eq: $locale } }) {
      ...Translations
    }
    allContentstackAssets(filter: { uid: { in: $jsonRTEImgUids } }) {
      nodes {
        uid
        url
        gatsbyImageData(layout: FULL_WIDTH, quality: 80)
      }
    }
    contentstackHeader(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...HeaderBasicFragment
    }
    contentstackExtendedHeader(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...ExtendedHeaderBasicFragment
    }
    contentstackHeaderWithSplitter(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...HeaderWithSplitterFragment
    }
    contentstackSettings(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...SettingsFragment
    }
    contentstackInfoBar(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...InfoBarFragment
    }
    contentstackFooter(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...FooterBasicFragment
    }
    contentstackNewFooter(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...NewFooterBasicFragment
    }
    contentstackFooterWithSplitter(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...FooterWithSplitterFragment
    }

    allContentstackL2ResourcePage(
      filter: { categories: { elemMatch: { uid: { in: $categoryUids } } } }
      sort: {
        fields: [page_additional_data___initial_publish_date]
        order: DESC
      }
      limit: 12
    ) {
      nodes {
        uid
        updated_at
        fields {
          fullPath
        }
        pageHeader: page_header {
          headingBlock: heading_block {
            headline
            descriptionRte: description_rte
          }
          image_element {
            image {
              url
              gatsbyImageData(width: 550)
            }
            alt
            aspectRatio: aspect_ratio
            objectFit: object_fit
            objectPosition: object_position
            rounded
            caption
          }
        }
        publishDetails: publish_details {
          locale
        }
        categories {
          title
          uid
          use_in_lables
        }
        use_fallback_image
        listing_image {
          image {
            url
            gatsbyImageData(width: 550)
          }
          alt
          aspectRatio: aspect_ratio
          objectFit: object_fit
          objectPosition: object_position
          rounded
          caption
        }
        page_additional_data {
          duration
          initial_publish_date
          resource_link
          content_type
        }
        listing_data {
          description
          label {
            content
            sr_prefix
            variant
            size
          }
        }
      }
    }
  }
`;

export default withContentstackPreview(
  L2EventPage,
  addBrandPostfixConditionally(
    'l2_Event_page',
    process.env.GATSBY_CONTENTSTACK_BRAND_TAG
  )
);

export { Head } from '~/common/StructuredData';
